import {
  Dialog as MUIDialog,
  DialogTitle,
  DialogContent,
  Box,
  SxProps,
} from '@mui/material';
import { PropsWithChildren, ReactNode } from 'react';

import ChevronLeft from '../../assets/icons/ChevronLeft';
import Button from '../../components/styledComponents/Button';
import colors from '../../constants/colors';
import { RemoveButton } from '../styledComponents/IconButtons';
import HiddenDialogButton from './HiddenDialogButton';

interface DialogProps extends React.PropsWithChildren {
  closeDialog: () => void;
  open: boolean;
  title: string | ReactNode;
  onBack?: () => void;
  fullScreenMobile?: boolean;
  sx?: SxProps;
  mobileFixToBottom?: boolean;
  showCloseButton?: boolean;
  ariaLabelledby?: string;
  keepMounted?: boolean;
}

const Dialog: React.FC<DialogProps> = ({
  children,
  closeDialog,
  open,
  title,
  onBack,
  fullScreenMobile,
  mobileFixToBottom,
  sx = {},
  showCloseButton = true,
  ariaLabelledby,
  keepMounted = false,
}: PropsWithChildren<DialogProps>) => {
  const dialogTitle =
    typeof title === 'string'
      ? `${title.split(' ').join('-')}-label`
      : undefined;
  return (
    <MUIDialog
      aria-labelledby={dialogTitle ?? ariaLabelledby}
      data-testid="dialog"
      open={open}
      keepMounted={keepMounted}
      onClose={closeDialog}
      sx={{
        backdropFilter: mobileFixToBottom ? 'none' : 'blur(5px)',
        ...sx,
      }}
      PaperProps={{
        sx: {
          bottom: { mobile: mobileFixToBottom ? 0 : null, tablet: null },
          borderRadius: {
            mobile: mobileFixToBottom ? '24px 24px 0px 0px' : 4,
            tablet: 4,
          },
          margin: 0,
          px: { mobile: 3, tablet: 4 },
          position: { mobile: 'fixed', tablet: 'relative' },
          width: { mobile: 1, tablet: 480 },
          ...(fullScreenMobile && {
            height: { mobile: '100%', tablet: 'auto' },
          }),
        },
      }}
    >
      {open && <HiddenDialogButton />}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: 1,
          mt: { mobile: 2, tablet: 3 },
        }}
      >
        {onBack ? (
          <Button
            data-test="dialogBackButton"
            startIcon={<ChevronLeft />}
            disableRipple
            variant="text"
            onClick={onBack}
            sx={{
              padding: 0,
              textTransform: 'none',
              color: colors.csaaDark,
              fontSize: 18,
              fontWeight: 400,
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '&:focus': {
                backgroundColor: 'transparent',
              },
            }}
          >
            Back
          </Button>
        ) : (
          <div /> // this is purely for styling so we can use space-between to position the close button
        )}
        {showCloseButton && (
          <RemoveButton
            aria-label={`Close ${dialogTitle} dialog`}
            disableRipple
            onClick={closeDialog}
          />
        )}
      </Box>
      {typeof title === 'string' ? (
        <DialogTitle
          id={dialogTitle}
          sx={{
            fontSize: { mobile: 18, tablet: 24 },
            fontWeight: 700,
            textAlign: 'center',
            p: 0,
          }}
        >
          {title}
        </DialogTitle>
      ) : (
        title
      )}
      <DialogContent sx={{ pt: 0, px: 0, pb: 3 }}>{children}</DialogContent>
    </MUIDialog>
  );
};

export default Dialog;
